import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { Link } from 'react-router-dom';

import { getNotifications, markEverythingAsRead, updateNotification } from '../../../../api/notifications';
import ConversationsListItem from '../conversations_list_item';
import { PhosphorIcon, PHOSPHOR_PATHS } from '../phosphor_icons';

import styles from '../new_conversation_room.module.css';

const MAX_NOTIFICATIONS_POPOVER_WIDTH = 380;
const NOTIFICATIONS_POPOVER_GUTTER = 16;

const isUnreadNotification = (notification) => notification.read === false || notification.isRead === false;

const notificationText = (notification) => {
  if (notification.custom_text) {
    return notification.custom_text;
  }

  const actor = notification.action_profile?.display_name;
  const phrase = notification.action_phrase;
  const gist = notification.gist;
  const otherCount = notification.num_other_distinct_users;

  if (actor && phrase && gist) {
    if (otherCount >= 1) {
      return `${actor} and ${otherCount} ${otherCount === 1 ? 'other' : 'others'} ${phrase} "${gist}"`;
    }

    return `${actor} ${phrase} "${gist}"`;
  }

  return notification.title || notification.body || 'Notification update';
};

function NewUiNotifications({ unreadNotificationCount = 0, onUnreadNotificationCountChange = () => {} }) {
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [popoverPosition, setPopoverPosition] = useState(null);

  const updatePopoverPosition = () => {
    if (!triggerRef.current) return;

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const availableWidth = Math.max(0, window.innerWidth - NOTIFICATIONS_POPOVER_GUTTER * 2);
    const width = Math.min(MAX_NOTIFICATIONS_POPOVER_WIDTH, availableWidth);
    const left = Math.max(
      NOTIFICATIONS_POPOVER_GUTTER,
      Math.min(triggerRect.right - width + 20, window.innerWidth - width - NOTIFICATIONS_POPOVER_GUTTER)
    );

    setPopoverPosition({
      left,
      top: triggerRect.bottom + 10,
      width,
    });
  };

  const loadNotifications = () => {
    setLoading(true);
    getNotifications((data) => {
      setNotifications(Array.isArray(data) ? data : []);
      setLoading(false);
    });
  };

  const openNotifications = () => {
    updatePopoverPosition();
    setOpen(true);
    loadNotifications();
  };

  const closeNotifications = () => {
    setOpen(false);
  };

  const toggleNotifications = () => {
    if (open) {
      closeNotifications();
      return;
    }

    openNotifications();
  };

  const markAllRead = () => {
    markEverythingAsRead();
    setNotifications((currentNotifications) =>
      currentNotifications.map((notification) => ({
        ...notification,
        read: true,
        isRead: true,
      }))
    );
    onUnreadNotificationCountChange(0);
  };

  const markNotificationRead = (notification) => {
    const unread = isUnreadNotification(notification);

    if (unread) {
      updateNotification(notification.id, { read: true });
      setNotifications((currentNotifications) => {
        const nextNotifications = currentNotifications.map((currentNotification) => {
          if (currentNotification.id !== notification.id) {
            return currentNotification;
          }

          return {
            ...currentNotification,
            read: true,
            isRead: true,
          };
        });

        onUnreadNotificationCountChange(nextNotifications.filter(isUnreadNotification).length);
        return nextNotifications;
      });
    }
  };

  useEffect(() => {
    if (!open) return undefined;

    updatePopoverPosition();

    const handleDocumentMouseDown = (event) => {
      if (panelRef.current?.contains(event.target) || triggerRef.current?.contains(event.target)) {
        return;
      }

      closeNotifications();
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        closeNotifications();
      }
    };

    window.addEventListener('resize', updatePopoverPosition);
    window.addEventListener('scroll', updatePopoverPosition, true);
    document.addEventListener('mousedown', handleDocumentMouseDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('resize', updatePopoverPosition);
      window.removeEventListener('scroll', updatePopoverPosition, true);
      document.removeEventListener('mousedown', handleDocumentMouseDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  const popover =
    open && typeof document !== 'undefined'
      ? ReactDOM.createPortal(
          <div
            ref={panelRef}
            className={styles.notifications_popover}
            role='dialog'
            aria-label='Notifications'
            style={popoverPosition || undefined}
          >
            <header className={styles.notifications_popover_header}>
              <div className={styles.notifications_popover_title_row}>
                <h2>Notifications</h2>
                {unreadNotificationCount > 0 && (
                  <span className={styles.notifications_unread_pill}>{unreadNotificationCount} new</span>
                )}
              </div>
              <div className={styles.notifications_popover_actions}>
                {unreadNotificationCount > 0 && (
                  <button type='button' className={styles.notifications_mark_read} onClick={markAllRead}>
                    Mark all read
                  </button>
                )}
                <button
                  type='button'
                  className={styles.notifications_close}
                  onClick={closeNotifications}
                  aria-label='Close notifications'
                >
                  <svg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'>
                    <path d='M4 4L12 12M12 4L4 12' stroke='currentColor' strokeWidth='2' strokeLinecap='round' />
                  </svg>
                </button>
              </div>
            </header>
            <div className={styles.notifications_popover_body}>
              {loading && <div className={styles.notifications_loading}>Loading notifications...</div>}
              {!loading && notifications.length === 0 && (
                <div className={styles.notifications_empty_state}>
                  <span className={styles.notifications_empty_icon} aria-hidden='true'>
                    <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
                      <path
                        d='M18 9.6C18 6.4 15.4 4 12 4C10.8 4 9.7 4.3 8.8 4.9M6.8 6.8C6.3 7.6 6 8.5 6 9.6V12.8L4.7 15.2C4.3 15.9 4.8 16.8 5.6 16.8H16.8M10 19C10.4 19.6 11.1 20 12 20C12.9 20 13.6 19.6 14 19M4 4L20 20'
                        stroke='currentColor'
                        strokeWidth='1.8'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      />
                    </svg>
                  </span>
                  <p className={styles.notifications_empty_title}>You're all caught up</p>
                  <p className={styles.notifications_empty_copy}>
                    New messages, call updates, and mentor activity will show up here.
                  </p>
                </div>
              )}
              {!loading && notifications.length > 0 && (
                <ul className={styles.notifications_list}>
                  {notifications.map((notification) => {
                    const unread = isUnreadNotification(notification);

                    return (
                      <li key={notification.id}>
                        <button
                          type='button'
                          className={`${styles.notification_row} ${unread ? styles.notification_row_unread : ''}`}
                          onClick={() => markNotificationRead(notification)}
                        >
                          {unread && <span className={styles.notification_unread_bar} aria-hidden='true' />}
                          <span className={styles.notification_row_body}>
                            <span className={styles.notification_row_title}>{notificationText(notification)}</span>
                            {notification.last_notified_at && (
                              <span className={styles.notification_row_time}>{notification.last_notified_at}</span>
                            )}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>,
          document.body
        )
      : null;

  return (
    <>
      <button
        ref={triggerRef}
        type='button'
        className={`${styles.convo_list_bell} ${open ? styles.convo_list_bell_open : ''}`}
        aria-label={unreadNotificationCount > 0 ? `Notifications, ${unreadNotificationCount} unread` : 'Notifications'}
        aria-expanded={open}
        onClick={toggleNotifications}
      >
        <PhosphorIcon
          path={unreadNotificationCount > 0 ? PHOSPHOR_PATHS.bell.fill : PHOSPHOR_PATHS.bell.regular}
          size={18}
        />
        {unreadNotificationCount > 0 && (
          <span className={styles.convo_list_notification_badge}>
            {unreadNotificationCount > 9 ? '9+' : unreadNotificationCount}
          </span>
        )}
      </button>
      {popover}
    </>
  );
}

export default function ConversationList({
  conversations,
  pathToMatch = '',
  selectedConversation,
  unreadNotificationCount = 0,
  onUnreadNotificationCountChange = () => {},
  headerAction = null,
  footer = null,
  isLoading = false,
}) {
  return (
    <>
      {conversations && (
        <div className={styles.convo_list_panel}>
          <div className={styles.convo_list_header}>
            <h5>Conversations</h5>
            <div className={styles.convo_list_header_actions}>
              {headerAction}
              <NewUiNotifications
                unreadNotificationCount={unreadNotificationCount}
                onUnreadNotificationCountChange={onUnreadNotificationCountChange}
              />
            </div>
          </div>
          <div
            className={`${styles.convo_list} ${isLoading ? styles.convo_list_loading : ''}`}
            role='list'
            aria-label='Conversations'
            aria-busy={isLoading ? 'true' : 'false'}
          >
            {conversations && conversations.length === 0 && (
              <div className={styles.convo_list_empty}>
                <p>No conversations yet. Connect with a mentor to start chatting.</p>
              </div>
            )}
            {conversations &&
              conversations.map((conversation, i) => (
                <div key={conversation.uuid || i} role='listitem'>
                  <Link to={`${pathToMatch}${conversation.uuid}`} className={styles.conversation_link}>
                    <ConversationsListItem conversation={conversation} selectedConversation={selectedConversation} />
                  </Link>
                </div>
              ))}
            {isLoading && (
              <div className={styles.convo_list_loading_overlay} role='status' aria-live='polite'>
                <span className={styles.convo_list_loading_spinner} aria-hidden='true' />
                <span>Loading conversations...</span>
              </div>
            )}
          </div>
          {footer && <div className={styles.convo_list_footer}>{footer}</div>}
        </div>
      )}
    </>
  );
}
